<template>
    <div class="entrance-wrapper" ref="entranceWrapper">
        <!-- 主要内容区域 -->
        <div class="entrance-main">
            <!-- 页面头部 -->
             <ClientOnly>
                <HallHeader :tab-name="tabName" :tabs="tabs" @tab-change="handleTabChange" />
            </ClientOnly>
            <!-- 房间卡片列表 -->
            <div class="main-box">
                <ClientOnly>
                    <template v-if="gameHalls && gameHalls.length">
                        <HallCard v-for="(hall, index) in gameHalls" :key="`${tabName}-${index}`" :hall="hall"
                            :index="index" :current-rate="tabName" @select="selectHall(hall, index)" />
                    </template>
                </ClientOnly>
            </div>
        </div>
        <!-- 底部固定区域 - 快速开始按钮 -->
        <HallFooter @join="quickJoin" :rtp="rtp" :isEntrance="true" />
    </div>
    <!--
        ↓ 历史“详情” 窗口，在此监听 route 参数变化并打开
        ↓ 主要服务于从管理员的后台界面进入的情况，后面管理员界面的数据没有厅 id，所以跳到入口页打开游戏历史 
    -->
    <CasinoHistoryModalGameDetali
        :gameDropValue="'niuniu'"
    />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount, watch } from 'vue'
import { toCloseLoading } from '@/layouts/gameCoat/ts/gameCoat';

import HallHeader from '../_components/HallHeader.vue'
import HallFooter from '../_components/HallFooter.vue'
import HallCard from '../_components/HallCard.vue'
import type { GameHall, TabItem } from '../ts/gameTps'
import { getHallsList, getMultipliers, fnGoIntoOneHall } from '../ts/niuniuCommon'
import { useGameSocket } from '@/socket/game'

const { EMIT_GAME_JOIN, EMIT_GAME_EXIT } = useGameSocket()

const entranceWrapper = ref<HTMLElement | null>(null)
const rtp = ref<string>('99.02%')

// 页面初始化时会自动选择第一个选项
const tabName = ref<string>(getMultipliers()[0]?.name || 'x3')

// 房间列表 - getHallsList()内部会根据当前币种(userStore.currentCurrency)获取对应的房间配置
const gameHalls = computed(() => {
    return getHallsList()
})
// 倍率选项，自动根据tabName标记active状态
const tabs = computed(() => {
    const multipliers = getMultipliers()
    return multipliers.map(tab => ({
        ...tab,
        actived: tab.name === tabName.value
    }))
})
/**
 * 选择房间并跳转
 * @param hall 选择的房间信息
 * @param index 房间索引
 * @returns void
 */
const selectHall = (hall: GameHall, index: number): void => {
    const rate = tabName.value ? tabName.value.replace('x', '') : '3'
    fnGoIntoOneHall(hall, rate, { query: { name: hall.name } });
}

/**
 * 选择倍率
 * @param selectedTab 选择的倍率标签
 */
const handleTabChange = (selectedTab: TabItem): void => {
    // 只需设置tabName，tabs计算属性会自动更新active状态
    if (selectedTab?.name) {
        tabName.value = selectedTab.name
    }
}

/**
 * 快速加入房间 - 随机选择一个可用房间并进入
 */
const quickJoin = (): void => {
    // 1、筛选出已 开通的房间
    const availableHalls = gameHalls.value.filter(hall => hall.enabled);
    if (availableHalls.length === 0) {
        showNegativeNotify({
            i18n: 'goldenFlower.hallDisabled',
        });
        return;
    }

    // 2、再根据已开通的房间 筛选出余额充足的房间
    const accessableHalls = availableHalls.filter(hall => hall.accessable);
    if (accessableHalls.length === 0) {
        showNegativeNotify({
            i18n: 'niuniu.lowBalance',
        }); 
        return;
    }

    // 3、随机选择一个可用房间
    const randomIndex = Math.floor(Math.random() * accessableHalls.length);
    const selectedHall = accessableHalls[randomIndex];
    // 进入选中的房间
    if (selectedHall) {
        // 调用选择房间方法进入房间
        const rate = tabName.value ? tabName.value.replace('x', '') : '3'
        fnGoIntoOneHall(selectedHall, rate, { query: { name: selectedHall.name } });
    }
}

/**
 * 调整窗口大小时更新宽度
 */
const onResize = (): void => {
    if (entranceWrapper.value && entranceWrapper.value.offsetWidth) {
        const baseWidth = 375
        const baseFontSize = 16
        
        const containerWidth = entranceWrapper.value.offsetWidth
        
        // 根据宽度计算基础 font-size
        let fontSize = (containerWidth / baseWidth) * baseFontSize
        
        // 设置 html 的 font-size 实现 rem 缩放
        document.documentElement.style.fontSize = `${fontSize}px`
    }
}
onBeforeMount(() => {
    toCloseLoading()
})

onMounted(() => {
    onResize()
    window.addEventListener("resize", onResize)

    // ↓ 进入大厅时调用 join 接口，参数传空
    // ↓ 这样当用户进入牛牛游戏大厅时，会调用 games.niuniu.join 接口通知服务器，服务器会将该用户标记为"在游戏中"。
    // ↓ 如果同一账号在其他窗口打开同一游戏，服务器就会向旧窗口发送 log_off code 8888 事件。
    EMIT_GAME_JOIN('niuniu', {}, (res) => {
        console.log('niuniu entrance join:', res)
    })

    // 初始化时自动选择第一个倍率选项
    const multipliers = getMultipliers()
    if (multipliers.length > 0) {
        // 检查当前tabName是否在可用选项中，如果不在则使用第一个
        const isValidTab = multipliers.some(m => m.name === tabName.value)
        if (!isValidTab) {
            tabName.value = multipliers[0].name
        }
    }
})

onBeforeUnmount(() => {
    window.removeEventListener("resize", onResize)
    // ↓ 离开大厅时调用 exit 接口，通知服务器用户已离开游戏
    EMIT_GAME_EXIT('niuniu')
})
</script>

<style scoped lang="scss">
.entrance-wrapper {
    position: relative;
    max-width: 500px;
    margin: 0 auto;
    max-height: calc(100vh - var(--header-heigh));
    height: calc(100vh - var(--header-heigh));
    display: flex;
    flex-direction: column;
    background-image: url(../img/hall-cover/cover-bg.webp);
    background-size: cover;
    background-repeat: no-repeat;
    overflow: hidden;
}

.entrance-main {
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.main-box {
    flex: 1;
    // min-height: 0;
    height: 100%;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    // gap 属性在低版本浏览器不支持，使用 margin 方案替代
    padding: 0.5rem;
    padding-bottom: 1.25rem;
    overflow-y: auto;
    overflow-x: hidden;
    
    // 为子元素添加间距（兼容低版本浏览器）
    > * + * {
        margin-top: 1.188rem;
    }
    
    /* 高屏幕时间距随高度动态变化 */
    // @media (min-height: 718px) {
    //     > * + * {
    //         margin-top: 8vh;
    //     }
    // }
    
    /* 滚动条美化 */
    scrollbar-width: thin;
    scrollbar-color: var(--color_grey-40) transparent;
    
    &::-webkit-scrollbar {
        width: 4px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: var(--color_grey-40);
        border-radius: 2px;
    }
    
    > * {
        flex-shrink: 0;
    }
    
    :deep(.hall-card) {
        flex-shrink: 0;
    }
}

.loading-placeholder,
.error-placeholder {
    width: 100%;
    padding: 20px 0;
    text-align: center;
    color: var(--color_grey-20);
    font-size: 16px;
}

.error-placeholder {
    color: var(--color_danger);
}

@media (min-height: 812px){
    .main-box{
        justify-content: space-around;
    }
}
</style>